import { useEffect, useRef, useState } from "react";
import { EventLink, evColor, fmtTime } from "./EventLink";

const HOUR = 48;
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const mins = (d) => d.getHours() * 60 + d.getMinutes();

function layout(evs) {
  const items = evs.map((ev) => { const s = new Date(ev.start), e = new Date(ev.end); return { ev, s: mins(s), e: Math.max(mins(s) + 20, sameDay(s, e) ? mins(e) : 1440) }; }).sort((a, b) => a.s - b.s || b.e - a.e);
  let cluster = [], end = -1;
  const flush = () => { const n = Math.max(...cluster.map((c) => c.col)) + 1; cluster.forEach((c) => { c.n = n; }); cluster = []; };
  items.forEach((it) => {
    if (it.s >= end && cluster.length) flush();
    const used = new Set(cluster.filter((c) => c.e > it.s).map((c) => c.col));
    let col = 0; while (used.has(col)) col++;
    it.col = col; cluster.push(it); end = Math.max(end, it.e);
  });
  if (cluster.length) flush();
  return items;
}

function NowLine() {
  const [m, setM] = useState(mins(new Date()));
  useEffect(() => { const t = setInterval(() => setM(mins(new Date())), 60000); return () => clearInterval(t); }, []);
  return <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: (m / 60) * HOUR }} data-testid="calendar-now-line"><div className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-[#ea4335]" /><div className="h-[2px] bg-[#ea4335]" /></div>;
}

function DayColumn({ day, evs, leads }) {
  return (
    <div className="relative border-l border-[var(--border)]" style={{ height: HOUR * 24 }} data-testid={`calendar-col-${day.toDateString().replace(/\s/g, "-")}`}>
      {Array.from({ length: 24 }, (_, h) => <div key={h} className="border-b border-[var(--border)]/70" style={{ height: HOUR }} />)}
      {sameDay(day, new Date()) && <NowLine />}
      {layout(evs).map(({ ev, s, e, col, n }) => {
        const h = ((e - s) / 60) * HOUR;
        return (
          <EventLink key={ev.id} ev={ev} leads={leads}
            className="absolute rounded-md px-1.5 py-0.5 text-left text-[11px] leading-tight cal-ev overflow-hidden hover:brightness-110 hover:z-30 transition-[filter] border border-black/40"
            style={{ top: (s / 60) * HOUR + 1, height: h - 2, left: `calc(${(col / n) * 100}% + 1px)`, width: `calc(${100 / n}% - 4px)`, background: evColor(ev) }}>
            <div className="font-medium truncate">{ev.title}</div>
            {h > 30 && <div className="opacity-90 truncate">{fmtTime(ev.start)} – {fmtTime(ev.end)}</div>}
            {h > 44 && ev.lead_ids.length > 0 && <div className="opacity-90 truncate">{ev.lead_ids.map((i) => leads[i].name || leads[i].email).join(", ")}</div>}
          </EventLink>
        );
      })}
    </div>
  );
}

export default function TimeGrid({ days, events, leads }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = HOUR * 7; }, [days.length]);
  const timed = (d) => events.filter((ev) => !ev.all_day && sameDay(new Date(ev.start), d));
  const allDay = (d) => events.filter((ev) => ev.all_day && sameDay(new Date(`${ev.start}T00:00:00`), d));
  const cols = { gridTemplateColumns: `56px repeat(${days.length}, minmax(${days.length > 1 ? 88 : 0}px, 1fr))` };
  const hasAllDay = days.some((d) => allDay(d).length);
  return (
    <div className="panel overflow-hidden flex flex-col" data-testid="calendar-timegrid">
      <div className="overflow-x-auto">
        <div className="min-w-fit">
          <div className="grid border-b border-[var(--border)]" style={cols}>
            <div className="text-[10px] text-neutral-500 self-end p-1">{Intl.DateTimeFormat().resolvedOptions().timeZone.split("/").pop()}</div>
            {days.map((d) => {
              const today = sameDay(d, new Date());
              return (
                <div key={d.toDateString()} className="text-center py-2 border-l border-[var(--border)]">
                  <div className={`text-[11px] uppercase tracking-wide ${today ? "text-[#8ab4f8]" : "text-neutral-400"}`}>{d.toLocaleDateString([], { weekday: "short" })}</div>
                  <div className={`mx-auto mt-0.5 w-10 h-10 grid place-items-center rounded-full text-xl ${today ? "bg-[#8ab4f8] text-black font-medium" : ""}`}>{d.getDate()}</div>
                  {hasAllDay && <div className="px-1 mt-1 space-y-0.5 min-h-[22px]">{allDay(d).map((ev) => <EventLink key={ev.id} ev={ev} leads={leads} className="block w-full truncate rounded px-1.5 py-0.5 text-[11px] text-left cal-ev" style={{ background: evColor(ev) }}>{ev.title}</EventLink>)}</div>}
                </div>
              );
            })}
          </div>
          <div ref={ref} className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 290px)", minHeight: 360 }}>
            <div className="grid" style={cols}>
              <div className="relative" style={{ height: HOUR * 24 }}>
                {Array.from({ length: 23 }, (_, h) => <div key={h} className="absolute right-2 text-[10px] text-neutral-500 -translate-y-1/2" style={{ top: (h + 1) * HOUR }}>{new Date(2000, 0, 1, h + 1).toLocaleTimeString([], { hour: "numeric" })}</div>)}
              </div>
              {days.map((d) => <DayColumn key={d.toDateString()} day={d} evs={timed(d)} leads={leads} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
